import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'src/context/AuthContext';
import { supabase } from 'src/lib/supabaseClient';
import { Button } from 'src/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/Card';
import { Badge } from 'src/components/ui/Badge';
import { Separator } from 'src/components/ui/Separator';
import { useToast } from 'src/hooks/use-toast';
import MeetingPreJoinInstructions from 'src/components/meetings/MeetingPreJoinInstructions';
import TeamsJoinDebugger from 'src/components/meetings/TeamsJoinDebugger';
import {
  Video,
  Clock,
  User,
  Calendar,
  ExternalLink,
  Shield,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  HelpCircle
} from 'lucide-react';

interface SessionData {
  id: string;
  scheduled_at: string;
  duration_min: number;
  status: string;
  mode: string;
  meeting_url?: string;
  student_id: string;
  tutor_id: string;
  session_type: string;
  student_profile?: {
    first_name: string;
    last_name: string;
    email?: string;
  };
  tutor_profile?: {
    first_name: string;
    last_name: string;
    email?: string;
  };
  topic?: {
    name: string;
    subject?: {
      name: string;
    };
  };
  subtopic?: {
    name: string;
  };
  batch?: {
    name: string;
  };
  session_details?: {
    meeting_metadata?: {
      provider: string;
      join_url: string;
      join_url_browser?: string;
      join_url_visit_teams?: string;
      join_url_direct_clean?: string;
      join_url_simple?: string;
      join_url_minimal?: string;
      meeting_id: string;
      debug_info?: {
        tenant_id?: string;
        creation_time?: string;
        api_response_keys?: string[];
        organizer_object_id?: string;
      };
    };
  };
  meeting_session?: {
    meeting_url?: string;
    join_url?: string;
  };
}

const JoinSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user, userType } = useAuth();
  const { toast } = useToast();
  
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (!sessionId || !user) {
      setError('Invalid session or user not authenticated');
      setLoading(false);
      return;
    }

    fetchSessionData();
  }, [sessionId, user]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch session with participant validation and topic/subtopic information
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          *,
          student_profile:profiles!sessions_student_id_fkey(first_name, last_name, email),
          tutor_profile:profiles!sessions_tutor_id_fkey(first_name, last_name, email),
          topic:topics(
            name,
            subject:subjects(name)
          ),
          subtopic:subtopics(name),
          batch:batches(name),
          session_details(meeting_metadata),
          meeting_session:meeting_sessions(meeting_url, join_url)
        `)
        .eq('id', sessionId)
        .single();

      if (sessionError) {
        throw new Error('Session not found');
      }

      // Validate user authorization
      const isAuthorized = 
        sessionData.student_id === user.id ||
        sessionData.tutor_id === user.id ||
        userType === 'admin';

      if (!isAuthorized) {
        throw new Error('You are not authorized to join this session');
      }

      // Check session status and timing
      const now = new Date();
      const sessionStart = new Date(sessionData.scheduled_at);
      const sessionEnd = new Date(sessionStart.getTime() + sessionData.duration_min * 60000);
      const canJoinEarly = sessionStart.getTime() - now.getTime() <= 15 * 60 * 1000; // 15 minutes early

      if (sessionData.status === 'cancelled') {
        throw new Error('This session has been cancelled');
      }

      if (sessionData.status === 'completed') {
        throw new Error('This session has already ended');
      }

      if (!canJoinEarly && now < sessionStart) {
        const minutesUntilStart = Math.ceil((sessionStart.getTime() - now.getTime()) / (1000 * 60));
        throw new Error(`Session starts in ${minutesUntilStart} minutes. You can join 15 minutes before the start time.`);
      }

      if (now > sessionEnd) {
        throw new Error('This session has ended');
      }

      setSession(sessionData);
    } catch (err) {
      console.error('Error fetching session:', err);
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinMeeting = async () => {
    if (!session) return;

    try {
      setJoining(true);

      // The scheduler persists one canonical URL in sessions.meeting_url. Both
      // participants use it verbatim so Teams, rather than this page, controls
      // lobby and admission behavior.
      const meetingUrl = session.meeting_url || session.meeting_session?.meeting_url || session.meeting_session?.join_url;

      if (!meetingUrl) {
        throw new Error(
          'A meeting link has not been set up for this session yet. Please ask the tutor or an administrator to create the Teams meeting.'
        );
      }

      // Note: We don't log 'participant_joined' here because clicking the join button
      // doesn't mean the user actually joined the meeting. The 'participant_joined' event
      // should only be logged when the user actually enters the Teams meeting, which
      // requires Microsoft Teams webhook integration or Graph API event subscriptions.
      console.log('Opening Teams meeting - participant_joined events should be tracked via Teams webhooks');

      // Open meeting in new window/tab
      window.open(meetingUrl, '_blank', 'noopener,noreferrer');

      toast({
        title: "Joining session",
        description: "Opening meeting in a new window...",
      });

      // Update session status if needed (ignore errors)
      try {
        if (session.status === 'scheduled') {
          console.log('Updating session status from scheduled to started');
          
          const { error: statusError } = await supabase
            .from('sessions')
            .update({ status: 'started' }) // Use 'started' which is allowed
            .eq('id', session.id);
          
          if (statusError) {
            console.log('Session status update failed (non-critical):', statusError);
          } else {
            console.log('Session status updated successfully to started');
          }
        }
      } catch (error) {
        console.log('Session status update failed (non-critical):', error);
      }

    } catch (err) {
      console.error('Error joining meeting:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to join meeting',
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  };

  
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getParticipantName = () => {
    if (!session) return '';
    
    if (userType === 'student') {
      return `${session.tutor_profile?.first_name} ${session.tutor_profile?.last_name}`;
    } else {
      return `${session.student_profile?.first_name} ${session.student_profile?.last_name}`;
    }
  };

  const getStudentName = () => {
    if (!session?.student_profile) return '';
    return `${session.student_profile.first_name} ${session.student_profile.last_name}`;
  };

  const getTutorName = () => {
    if (!session?.tutor_profile) return '';
    return `${session.tutor_profile.first_name} ${session.tutor_profile.last_name}`;
  };

  const getSessionStatus = () => {
    if (!session) return null;

    const now = new Date();
    const sessionStart = new Date(session.scheduled_at);
    const sessionEnd = new Date(sessionStart.getTime() + session.duration_min * 60000);

    if (now < sessionStart) {
      return { status: 'upcoming', color: 'blue', text: 'Upcoming' };
    } else if (now >= sessionStart && now <= sessionEnd) {
      return { status: 'live', color: 'green', text: 'Live Now' };
    } else {
      return { status: 'ended', color: 'gray', text: 'Ended' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Unable to Join Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{error}</p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button 
                onClick={() => navigate('/student/dashboard')}
                className="flex-1"
              >
                Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const sessionStatus = getSessionStatus();
  const { date, time } = formatDateTime(session.scheduled_at);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Main Session Card */}
          <Card className="w-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Join Session
                </CardTitle>
                {sessionStatus && (
                  <Badge 
                    variant={sessionStatus.color === 'green' ? 'default' : 'secondary'}
                    className={sessionStatus.color === 'green' ? 'bg-green-100 text-green-800' : ''}
                  >
                    {sessionStatus.text}
                  </Badge>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Session Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{time} ({session.duration_min} minutes)</span>
                </div>
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    {userType === 'student' ? 'Tutor: ' : 'Student: '}
                    {getParticipantName()}
                  </span>
                </div>
              </div>

              <Separator />

              <Button
                onClick={handleJoinMeeting}
                disabled={joining || sessionStatus?.status === 'ended'}
                className="w-full button-gradient"
                size="lg"
              >
                {joining ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Join Teams Meeting
                  </>
                )}
              </Button>

              {/* Security Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Secure Session</p>
                    <p className="text-blue-600">This session is private and only accessible to authorized participants.</p>
                  </div>
                </div>
              </div>

              {/* Debug Component - Show in development or for admins */}
              {(import.meta.env.DEV || userType === 'admin') && session.session_details?.meeting_metadata && (
                <TeamsJoinDebugger
                  sessionId={session.id}
                  meetingMetadata={session.session_details.meeting_metadata}
                />
              )}

              {/* Additional Actions (single location) */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Go Back
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/${userType}/dashboard`)}
                  className="flex-1"
                >
                  Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Instructions Panel - Desktop only, concise */}
          <div className="hidden lg:block">
            <MeetingPreJoinInstructions
              studentName={userType === 'student' ? getStudentName() : undefined}
              sessionTitle={`Tutoring Session - ${getParticipantName()}`}
              tutorName={userType === 'student' ? getTutorName() : undefined}
              // Only show joining steps, not session info or join button
            />
          </div>
        </div>
        {/* Instructions Panel - Mobile/Small Screens, concise */}
        <div className="lg:hidden mt-6">
          <Button
            variant="outline"
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full mb-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              <span>How to Join Instructions</span>
            </div>
            {showInstructions ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          {showInstructions && (
            <div className="mb-6">
              <MeetingPreJoinInstructions
                studentName={userType === 'student' ? getStudentName() : undefined}
                sessionTitle={`Tutoring Session - ${getParticipantName()}`}
                tutorName={userType === 'student' ? getTutorName() : undefined}
                // Only show joining steps, not session info or join button
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinSession;
